
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { RotateCcw, Clock, CheckCircle, XCircle, CreditCard, Mail, Phone, Shield, AlertCircle, Star } from "lucide-react";

const RefundPolicy = () => {
  return (
    <Layout>
      <Helmet>
        <title>Refund Policy - Easy Returns & Money Back Guarantee | TrueCore Gadgets</title>
        <meta name="description" content="TrueCore Gadgets refund policy - 7-day return window, hassle-free refunds, money back guarantee. Easy returns on tablets, PCs, and smart devices." />
        <meta name="keywords" content="TrueCore refund policy, return policy, money back guarantee, 7 day return, refund process, easy returns, tablet return, PC return" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://truecoregadgets.com/refund-policy" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Refund Policy - Easy Returns & Money Back Guarantee | TrueCore Gadgets" />
        <meta property="og:description" content="Hassle-free refunds and easy returns within 7 days. Money back guarantee on all TrueCore devices." />
        <meta property="og:url" content="https://truecoregadgets.com/refund-policy" />
        <meta property="og:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Refund Policy - TrueCore Gadgets" />
        <meta name="twitter:description" content="Easy returns and hassle-free refunds within 7 days." />
        <meta name="twitter:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4 max-w-5xl">
        <div className="text-center mb-12">
          <RotateCcw className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-bold gradient-text mb-4">Refund & Return Policy</h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We stand behind the quality of our products. If you're not completely satisfied with your purchase, we offer a comprehensive return and refund policy designed to give you peace of mind.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <Shield className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-800 mb-2">7-Day Guarantee</h3>
            <p className="text-green-700 text-sm">Money back guarantee on all products within 7 days of delivery</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <RotateCcw className="h-12 w-12 text-blue-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Free Returns</h3>
            <p className="text-blue-700 text-sm">We provide prepaid return labels for eligible returns</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
            <Star className="h-12 w-12 text-purple-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-purple-800 mb-2">Quality Promise</h3>
            <p className="text-purple-700 text-sm">Full refund if products don't meet our quality standards</p>
          </div>
        </div>

        <div className="bg-primary/10 rounded-lg p-8 mb-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Our Commitment to Customer Satisfaction</h2>
          <p className="text-lg text-muted-foreground">
            At TrueCore Gadgets, customer satisfaction is our top priority. We want you to be completely happy with your purchase. If for any reason you're not satisfied, our flexible return policy ensures you can shop with confidence.
          </p>
        </div>

        <div className="space-y-12">
          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Return Eligibility</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600 mr-4" />
                  <h3 className="text-xl font-semibold">Eligible for Return</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Products returned within 7 days of delivery
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Items in original, unused condition with no signs of wear
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    All original packaging, accessories, and documentation included
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Product serial numbers match original order
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Valid purchase receipt or order confirmation
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    No physical damage or unauthorized modifications
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Devices restored to factory settings (if applicable)
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Protective films and seals intact where applicable
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <XCircle className="h-8 w-8 text-red-600 mr-4" />
                  <h3 className="text-xl font-semibold">Not Eligible for Return</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Return request initiated after 7 days from delivery
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Physical damage, scratches, or signs of wear and tear
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Missing accessories, cables, or original packaging
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Custom-built or personalized products
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Software installations or data stored on devices
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Products with broken or tampered warranty seals
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Bulk orders over ₹5,00,000 (subject to special terms)
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    Products purchased with special discounts or clearance sales
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Step-by-Step Return Process</h2>
            
            <div className="bg-muted/50 rounded-lg p-8">
              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">1</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Initiate Return Request</h3>
                    <p className="text-muted-foreground mb-3">Contact our customer service team within 7 days of delivery to start your return process.</p>
                    <div className="bg-card p-4 rounded border">
                      <p className="text-sm"><strong>Required Information:</strong></p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Order number and purchase date</li>
                        <li>• Product model and serial number</li>
                        <li>• Reason for return</li>
                        <li>• Photos of the product (if damaged)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">2</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Return Authorization & Instructions</h3>
                    <p className="text-muted-foreground mb-3">We'll review your request and provide return authorization within 24 hours.</p>
                    <div className="bg-card p-4 rounded border">
                      <p className="text-sm"><strong>You'll Receive:</strong></p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Return authorization number (RMA)</li>
                        <li>• Prepaid shipping label</li>
                        <li>• Packaging instructions</li>
                        <li>• Return tracking information</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">3</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Package & Ship</h3>
                    <p className="text-muted-foreground mb-3">Carefully package your item and ship it back using our provided label.</p>
                    <div className="bg-card p-4 rounded border">
                      <p className="text-sm"><strong>Packaging Requirements:</strong></p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Use original packaging when possible</li>
                        <li>• Include all accessories and documentation</li>
                        <li>• Secure packaging to prevent damage</li>
                        <li>• Attach return label clearly</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">4</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Inspection & Processing</h3>
                    <p className="text-muted-foreground mb-3">Our quality team inspects returned items and processes eligible refunds.</p>
                    <div className="bg-card p-4 rounded border">
                      <p className="text-sm"><strong>Inspection Criteria:</strong></p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Condition verification</li>
                        <li>• Functionality testing</li>
                        <li>• Accessory completeness check</li>
                        <li>• Documentation review</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">5</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Refund Completion</h3>
                    <p className="text-muted-foreground mb-3">Once approved, your refund is processed to your original payment method.</p>
                    <div className="bg-card p-4 rounded border">
                      <p className="text-sm"><strong>Refund Timeline:</strong></p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• Credit/Debit Cards: 5-7 business days</li>
                        <li>• Bank Transfer: 3-5 business days</li>
                        <li>• Digital Wallets: 1-3 business days</li>
                        <li>• Cash on Delivery: Bank transfer within 7 days</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Refund Methods & Timeline</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <CreditCard className="h-8 w-8 text-primary mr-4" />
                  <h3 className="text-xl font-semibold">Available Refund Methods</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded">
                    <span className="font-medium">Original Payment Method</span>
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">Preferred</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded">
                    <span className="font-medium">Bank Transfer (NEFT/RTGS)</span>
                    <span className="text-sm text-muted-foreground">For cash payments</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded">
                    <span className="font-medium">Store Credit</span>
                    <span className="text-sm text-muted-foreground">Optional, never expires</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded">
                    <span className="font-medium">Digital Wallet</span>
                    <span className="text-sm text-muted-foreground">UPI, Paytm, etc.</span>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <Clock className="h-8 w-8 text-primary mr-4" />
                  <h3 className="text-xl font-semibold">Processing Timeline</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Return Processing</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Return authorization: Within 24 hours</li>
                      <li>• Inspection completion: 2-3 business days</li>
                      <li>• Refund approval: Same day after inspection</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Refund Completion</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Credit/Debit cards: 5-7 business days</li>
                      <li>• Net banking: 3-5 business days</li>
                      <li>• UPI/Digital wallets: 1-3 business days</li>
                      <li>• Bank transfer: 3-7 business days</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Special Circumstances</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <AlertCircle className="h-8 w-8 text-orange-600 mr-4" />
                  <h3 className="text-xl font-semibold">Damaged or Defective Products</h3>
                </div>
                <div className="space-y-3 text-muted-foreground">
                  <p>If you receive a damaged or defective product:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Contact us immediately upon delivery</li>
                    <li>Take photos of the damage or defect</li>
                    <li>We'll arrange immediate replacement or full refund</li>
                    <li>Extended return window of 14 days for defective items</li>
                    <li>Free return shipping and expedited processing</li>
                  </ul>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border">
                <div className="flex items-center mb-4">
                  <Shield className="h-8 w-8 text-green-600 mr-4" />
                  <h3 className="text-xl font-semibold">Bulk Orders & Enterprise Sales</h3>
                </div>
                <div className="space-y-3 text-muted-foreground">
                  <p>Special terms for large orders:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Orders over ₹5,00,000 require pre-approval for returns</li>
                    <li>Custom configurations may have different return terms</li>
                    <li>Dedicated account manager for return processing</li>
                    <li>Flexible exchange options for business customers</li>
                    <li>Warranty replacement preferred over refunds</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Return Shipping Information</h2>
            
            <div className="bg-card p-8 rounded-lg shadow-md border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Shipping Guidelines</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      We provide prepaid return shipping labels for all eligible returns
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Returns are fully insured against loss or damage during transit
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Use the original packaging whenever possible to ensure safe delivery
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Include all accessories, cables, and documentation in the package
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Tracking information is provided for all return shipments
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Return Address</h3>
                  <div className="bg-muted/30 p-4 rounded border">
                    <p className="font-medium mb-2">TrueCore Gadgets Returns Center</p>
                    <p className="text-sm text-muted-foreground">
                      Truecore Innovations<br/>
                      Returns Processing Center<br/>
                      Mumbai, Maharashtra, India<br/>
                      <strong>RMA Required:</strong> Include your return authorization number
                    </p>
                  </div>
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Important:</strong> Do not ship returns without a valid RMA number. Packages without RMA may be refused or delayed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6 text-primary">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">Can I return a product if I simply changed my mind?</h3>
                <p className="text-muted-foreground">Yes, we offer a 7-day no-questions-asked return policy. As long as the product is in original condition with all accessories, you can return it for any reason.</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">What if my product develops a fault after the 7-day return period?</h3>
                <p className="text-muted-foreground">Products are covered by our comprehensive warranty policy. Defective items can be repaired or replaced under warranty terms, which extend far beyond the return period.</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">Do I need to pay for return shipping?</h3>
                <p className="text-muted-foreground">No, we provide prepaid return shipping labels for all eligible returns. You won't incur any shipping costs for returning products to us.</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">How long does the refund process take?</h3>
                <p className="text-muted-foreground">Once we receive and inspect your return, refunds are processed within 1-2 business days. The time to appear in your account depends on your payment method, typically 3-7 business days.</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">Can I exchange a product instead of returning it?</h3>
                <p className="text-muted-foreground">Currently, we process returns and refunds rather than direct exchanges. However, you can return the original product and place a new order for the desired item.</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-2 text-foreground">What happens if my return is not approved?</h3>
                <p className="text-muted-foreground">If a return doesn't meet our criteria, we'll contact you to explain the reason and offer alternative solutions. The product will be returned to you at no charge.</p>
              </div>
            </div>
          </section>
        </div>

        <div className="bg-card p-8 rounded-lg shadow-md border text-center mt-12">
          <h2 className="text-2xl font-bold mb-6">Need Help with Your Return?</h2>
          <p className="text-muted-foreground mb-6">
            Our dedicated customer service team is here to assist you with any questions about returns or refunds. We're committed to making the process as smooth as possible.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center space-y-2">
              <Mail className="text-primary h-8 w-8" />
              <span className="font-medium">support@truecoregadgets.com</span>
              <span className="text-sm text-muted-foreground">Email Support</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Phone className="text-primary h-8 w-8" />
              <span className="font-medium">022 6962 1901</span>
              <span className="text-sm text-muted-foreground">Phone Support</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Clock className="text-primary h-8 w-8" />
              <span className="font-medium">Mon-Fri, 9 AM-6 PM</span>
              <span className="text-sm text-muted-foreground">Support Hours (IST)</span>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-primary/10 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Important:</strong> This return policy applies exclusively to direct purchases from truecoregadgets.com. 
              Products purchased from authorized resellers may have different return terms.<br/>
              <strong>Last updated:</strong> June 2023
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RefundPolicy;
